import { FC, useCallback, useEffect, useRef, useState } from 'react';

import { DistributorOutletListFilter } from '@app/api/models/distributor';
import { QueryTable } from '@app/components/common/table';
import { QueryTableParams } from '@app/components/common/table/types';
import { stringifySort } from '@app/components/common/table/utils';
import { useHeader } from '@app/components/common/navigation/hooks/header-service';
import { useApi } from '@app/hooks';

import { DistributorOutletRow, useColumns } from './columns';
import { EditDistributorOutlet } from './edit';

const toText = (value: unknown) => {
  if (value === undefined || value === null || value === '' || Array.isArray(value)) {
    return undefined;
  }
  return String(value);
};

const toNumber = (value: string | string[] | number[] | undefined) => {
  const text = toText(value);
  if (!text) {
    return undefined;
  }
  const number = Number(text);
  return Number.isFinite(number) ? number : undefined;
};

export const DistributorOutletsTable: FC = () => {
  useHeader('ТТ Дистрибьюторов', []);

  const api = useApi();
  const distributorNamesRef = useRef<Map<number, string>>(new Map());
  const segmentNamesRef = useRef<Map<number, string>>(new Map());
  const guidesLoadedRef = useRef(false);
  const [segments, setSegments] = useState<EntityBase[]>([]);
  const [editOpen, setEditOpen] = useState(false);
  const [current, setCurrent] = useState<DistributorOutletRow>();

  const columns = useColumns({
    onEdit: (item) => {
      setCurrent(item);
      setEditOpen(true);
    },
  });

  const loadGuides = useCallback(async () => {
    if (guidesLoadedRef.current) {
      return;
    }

    const [distributorsRes, segmentsRes] = await Promise.all([
      api.distributors.getDistributors({
        limit: 100000,
        offset: 0,
        sort: '+id',
        filter: {},
      }),
      api.guides.get({ name: 'outlet_segment' }),
    ]);

    distributorNamesRef.current = new Map(
      (distributorsRes?.data?.data || []).map((item) => [
        Number(item.id),
        item.name || item.legal_name || '',
      ])
    );
    const segmentItems = segmentsRes?.data || [];
    segmentNamesRef.current = new Map(
      segmentItems.map((item) => [Number(item.id), item.name || ''])
    );
    setSegments(segmentItems);
    guidesLoadedRef.current = true;
  }, [api]);

  useEffect(() => {
    loadGuides();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = useCallback(async ({
    pagination,
    filterRaw,
    sort,
  }: QueryTableParams<DistributorOutletRow>) => {
    await loadGuides();

    const filter: DistributorOutletListFilter = {
      distributor_id: toNumber(filterRaw.distributor_id),
      distributor_outlet_id: toText(filterRaw.distributor_outlet_id),
      distributor_outlet_name: toText(filterRaw.distributor_outlet_name),
      distributor_outlet_title: toText(filterRaw.distributor_outlet_title),
      distributor_outlet_address: toText(filterRaw.distributor_outlet_address),
      distributor_outlet_inn: toText(filterRaw.distributor_outlet_inn),
      subnet: toText(filterRaw.subnet),
      status: toText(filterRaw.status),
      segment_id: toNumber(filterRaw.segment_id),
      route_number_id: toNumber(filterRaw.route_number_id),
    };

    const response = await api.distributors.getDistributorOutlets({
      limit: pagination.pageSize,
      offset: pagination.pageSize * (pagination.page - 1),
      sort: stringifySort(sort),
      filter,
    });

    const items = response?.data?.data || [];

    return {
      code: response?.code,
      total: response?.data?.total || 0,
      data: items.map((item) => ({
        ...item,
        distributor_name: distributorNamesRef.current.get(Number(item.distributor_id)) || '',
        segment_name: item.segment_id
          ? segmentNamesRef.current.get(Number(item.segment_id)) || ''
          : '',
      })),
      message: response?.message,
    };
  }, [api, loadGuides]);

  const onClose = () => {
    setEditOpen(false);
    setCurrent(undefined);
  };

  return (
    <QueryTable<DistributorOutletRow>
      title="ТТ Дистрибьюторов"
      columns={columns}
      fetchData={fetchData}
      onCreateClick={() => {
        setCurrent(undefined);
        setEditOpen(true);
      }}
    >
      <EditDistributorOutlet
        open={editOpen}
        initial={current}
        segments={segments}
        onClose={onClose}
      />
    </QueryTable>
  );
};

export default DistributorOutletsTable;
